import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, SubHeader, Container, Card, Form, Input, Button } from '../../core/styles/styledComponents';
import styled from 'styled-components';
import { theme } from '../../core/styles/theme';
import { useAuth } from '../../context/AuthContext';

const Logo = styled.img`
  height: 50px;
`;

const RegisterContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.background.default};
`;

const RegisterCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  margin-top: ${theme.spacing.lg};
`;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await register(formData.firstName, formData.lastName, formData.email, formData.password);
      navigate('/login');
    } catch (error) {
      // Error is already handled by the AuthContext
      console.error('Registration failed:', error);
    }
  };

  return (
    <>
      <Header>
        <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
      </Header>
      <RegisterContainer>
        <RegisterCard>

          <h2 style={{ marginBottom: theme.spacing.lg }}>Join EureekaBI today</h2>
          <Form onSubmit={handleSubmit}>
            <Input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {errors.firstName && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.firstName}</div>}

            <Input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {errors.lastName && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.lastName}</div>}

            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {errors.email && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.email}</div>}

            <Input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {errors.password && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.password}</div>}

            <Input
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {errors.confirmPassword && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.confirmPassword}</div>}

            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
            <Button type="button" variant="minimal" onClick={() => navigate('/login')} disabled={isLoading}>
              Already have an account? Sign In
            </Button>
          </Form>
        </RegisterCard>
      </RegisterContainer>
    </>
  );
};

export default RegisterPage; 